import { Fragment, h } from '@atomify/jsx';
import { Button } from '@atoms/button/button';
import { Image, ImageInterface } from '@atoms/image';
import { useTranslate } from '@utilities/hooks';

export interface VideoData {
    id: string;
    platform: 'vimeo' | 'youtube';
    instanceId: string;
    noschema?: boolean;
    classes?: string;
    startTime?: string;
    buttons?: boolean;
    muted?: boolean;
    autoplay?: boolean;
    playsInline?: boolean;
    controls?: boolean;
    loop?: boolean;
    closedCaptions?: any;
    title?: string;
    description?: string;
    isPlaylist?: boolean;
    totalTime?: string;
    image?: ImageInterface;
    thumbnail?: string;
    embedUrl?: string;
    uploadDate?: string;
    previewText?: string;
    ignoreViewport?: boolean;
}

export const Video = (data: VideoData): Element => {
    const videoProps = {
        class: `c-video ${data.classes ? data.classes : ''}`.trim(),
        ...(data.noschema && {
            itemprop: 'video',
            itemscope: true,
            itemtype: 'http://schema.org/VideoObject',
        }),
        id: `video-${data.instanceId}`,
        ['data-video-platform']: data.platform,
        ...(data.id && { ['data-video-id']: data.id }),
        ...(data.startTime && { ['data-video-time']: data.startTime }),
        ...(data.muted && { ['data-video-muted']: data.muted ? '1' : '0' }),
        ...(data.autoplay && { ['data-video-autoplay']: data.autoplay ? '1' : '0' }),
        ...(data.playsInline && { ['data-video-playsinline']: data.playsInline ? '1' : '0' }),
        ...(data.controls && { ['data-video-controls']: data.controls ? '1' : '0' }),
        ...(data.loop && { ['data-video-loop']: data.loop ? '1' : '0' }),
        ...(data.closedCaptions && {
            ['data-video-closedcaptions']: JSON.stringify(data.closedCaptions),
        }),
        ...(data.title && { ['aria-label']: data.title }),
        ['is-playlist']: !!data.isPlaylist,
        ['ignore-viewport']: !!data.ignoreViewport,
    };
    return (
        <bpd-video {...videoProps} role="application" js-hook-video>
            {data.noschema && (
                <Fragment>
                    {data.title && <meta itemprop="name" content={data.title} />}
                    {data.description && <meta itemprop="description" content={data.description} />}
                    {data.totalTime && <meta itemprop="duration" content={data.totalTime} />}
                    {data.thumbnail && <meta itemprop="thumbnailUrl" content={data.thumbnail} />}
                    {data.embedUrl && <meta itemprop="embedUrl" content={data.embedUrl} />}
                    {data.uploadDate && <meta itemprop="uploadDate" content={data.uploadDate} />}
                </Fragment>
            )}

            <div class="video__player" js-hook-video-player />
            <div {...{ [`uc-${data.platform}-banner`]: true }} />

            {data.buttons && !data.previewText && (
                <button class="video__button video__button--play" js-hook-video-play>
                    <span class="video__button-icon"></span>
                    <span class="u-sr-only">{useTranslate('Play')}</span>
                </button>
            )}

            {data.previewText && (
                <Button
                    classes="video__play-text u-color--white"
                    label={data.previewText}
                    hook="video-play"
                />
            )}

            {data.image && <Image {...data.image} />}
        </bpd-video>
    );
};
